import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_FIELDS } from './model';

/**
 * E48 · the hub's functional-analysis commands — SetTargetBehavior, PlanContingencyChange, RetireTargetBehavior with the client
 * (`subject` = the client; needs a granted relation) — each ONE deliberate act (a button), posted with `errorNotification:
 * false` so the screen reports the outcome itself (portal conventions): a CODED refusal (`fa.errors.FA_*`, already in the
 * therapist's language) is shown verbatim; a null answer is a refusal, never a save; anything else is the screen's own copy
 * plus Sentry.
 */
export type FaOperation =
  | 'setTargetBehavior'
  | 'planContingencyChange'
  | 'retireTargetBehavior';

const INPUT_TYPE: Record<FaOperation, string> = {
  setTargetBehavior: 'SetTargetBehaviorInput!',
  planContingencyChange: 'PlanContingencyChangeInput!',
  retireTargetBehavior: 'RetireTargetBehaviorInput!',
};

export type FaCommandResult =
  | { ok: true; id: string | null; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useFaCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    id?: string;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: FaOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<FaCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: COMMAND_FIELDS,
            variables: {
              input: {
                name: 'input',
                type: INPUT_TYPE[operation],
                value: input,
              },
            },
          },
        });
        const data = res?.data;
        if (!data) {
          return { ok: false, code: undefined, message: fallback };
        }
        return { ok: true, id: data.id ?? null, recorded: !!data.recorded };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
